"use client";

import { usePostHog } from "@posthog/next";
import { useRouter } from "next/navigation";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SubscriptionStatus } from "@/enums/stripe";
import { UserRoles } from "@/enums/user";
import type { AuthSeedResult, AuthzData, PermissionsData } from "@/lib/auth-seed";
import { AgencyStatusResponse } from "@/types/agencies";
import { apiLive } from "@/lib/api";
import { ProductRolesState } from "@/utils/common/auth";
import { ClearAllCache } from "@/utils/common/cache";
import {
  POSTHOG_PERMISSIONS_FLAG_PROPERTY,
} from "@/utils/common/posthogPermissionFlags";
import { isEuropeRegion } from "@/utils/common/region";
import { GetRestrictions } from "@/utils/common/restrictions";
import { User } from "@/utils/common/userComparison";
import { identifyCannyUser } from "@/utils/changelog/canny";
import { FORCE_TOKEN_REFRESH_HEADER } from "@/utils/api/auth0RolePropagation";
import { TOKEN_REFRESHED_EVENT } from "@/utils/common/logout";
import { setAuthzDeniedIdentity } from "@/utils/authz/captureAuthzDenied";
import { isSafeInternalPath } from "@/utils/url";
import { useAuthSeedSync } from "./useAuthSeedSync";

// ────────────────────────────────────────────────────────────────────────────
// Context type — same interface as old AuthContextProvider
// ────────────────────────────────────────────────────────────────────────────

export type AuthContextValue = {
  login: () => Promise<void>;
  logout: () => Promise<void>;
  signup: () => Promise<void>;
  renewToken: (options?: { force?: boolean }) => Promise<void>;
  renewStatus: () => Promise<void>;
  switchOrg: (orgId: string, returnTo?: string) => Promise<void>;
  /**
   * Re-run the server layout's `buildAuthSeed()` via `router.refresh()`.
   * Permissions, authz, roles, and product seats adopt the fresh seed.
   */
  refreshAuthSeed: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | undefined;
  subscription: AgencyStatusResponse;
  isFreeTrial: boolean;
  role: UserRoles;
  rawRoles: string[];
  productRolesState: ProductRolesState;
  permissions: PermissionsData;
  /** Raw Galactus scopes when `me/permissions` succeeded; empty otherwise. */
  scopes: string[];
  authz: AuthzData;
  /** True when Galactus returned an authoritative `me/permissions` body. */
  scopesAuthoritative: boolean;
  /** True when first-login Galactus provisioning has not landed yet. */
  provisioningPending: boolean;
  /**
   * True when the seed could not resolve real permissions and fell back to the empty matrix.
   * Surfaces here so the route guard can stop enforcing rather than lock everyone out of every
   * page during a Galactus outage.
   */
  permissionsDegraded: boolean;
};

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuthContext = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuthContext must be used within AuthProvider");
  }
  return ctx;
};

// Re-export types so existing imports from AuthContextProvider still work
export { type ProductRolesState } from "@/utils/common/auth";
export { type User } from "@/utils/common/userComparison";

// ────────────────────────────────────────────────────────────────────────────
// Default subscription when status hasn't been fetched yet
// ────────────────────────────────────────────────────────────────────────────

const DEFAULT_SUBSCRIPTION: AgencyStatusResponse = {
  status: SubscriptionStatus.active,
  canAccess: true,
  restrictions: GetRestrictions(null),
  flags: { needs_signing: false },
  hasUsage: true,
};

const DEFAULT_PERMISSIONS: PermissionsData = {};

const DEFAULT_AUTHZ: AuthzData = {
  navContainer: "no_access",
  isOrgGailGptV1: false,
  productsAllPausedOrCanceled: false,
  isAdminWithNoRoles: false,
};

// ────────────────────────────────────────────────────────────────────────────
// Provider
// ────────────────────────────────────────────────────────────────────────────

export function AuthProvider({
  children,
  authSeed,
}: Readonly<{
  children: React.ReactNode;
  authSeed: AuthSeedResult;
}>) {
  const posthog = usePostHog();
  const router = useRouter();
  // Derive initial state from the server-provided seed — synchronous, no loading
  const initialAuth = authSeed.isAuthenticated ? authSeed : null;
  const agencyStatusFetched = useRef(false);

  const [user] = useState<User | undefined>(() => (initialAuth ? initialAuth.user : undefined));
  const [role, setRole] = useState<UserRoles>(() => initialAuth?.role ?? UserRoles.User);
  const [rawRoles, setRawRoles] = useState<string[]>(() => initialAuth?.rawRoles ?? []);
  const [productRolesState, setProductRolesState] = useState<ProductRolesState>(() => ({
    roles: initialAuth?.productRoles,
    loaded: true,
  }));
  const [permissions, setPermissions] = useState<PermissionsData>(
    () => initialAuth?.permissions ?? DEFAULT_PERMISSIONS
  );
  const [scopes, setScopes] = useState<string[]>(() => initialAuth?.scopes ?? []);
  const [authz, setAuthz] = useState<AuthzData>(() => initialAuth?.authz ?? DEFAULT_AUTHZ);
  const [scopesAuthoritative, setScopesAuthoritative] = useState<boolean>(
    () => initialAuth?.scopesAuthoritative ?? false
  );
  const [provisioningPending, setProvisioningPending] = useState<boolean>(
    () => initialAuth?.provisioningPending ?? false
  );
  // Unauthenticated trees have no matrix to degrade from, so treat "no seed" as degraded too.
  const [permissionsDegraded, setPermissionsDegraded] = useState<boolean>(
    () => initialAuth?.permissionsDegraded ?? true
  );
  const [agencyStatus, setAgencyStatus] = useState<AgencyStatusResponse | null>(null);
  const [identifiedUserId, setIdentifiedUserId] = useState<string | undefined>();

  const isAuthenticated = !!initialAuth;

  useAuthSeedSync(authSeed, {
    setPermissions,
    setScopes,
    setAuthz,
    setScopesAuthoritative,
    setProvisioningPending,
    setPermissionsDegraded,
    setRole,
    setRawRoles,
    setProductRolesState,
  });

  // ── Navigation actions ──────────────────────────────────────────────────

  const login = useCallback(async () => {
    const url = globalThis.location.href;
    const inviteMatches = /invitation=([^&]+)/.exec(url);
    const orgMatches = /organization=([^&]+)/.exec(url);

    let loginUrl = "/auth/login";
    const params = new URLSearchParams();

    if (inviteMatches && orgMatches) {
      params.set("organization", orgMatches[1]);
      params.set("invitation", inviteMatches[1]);
    }

    params.set("returnTo", globalThis.location.pathname);

    if (params.toString()) {
      loginUrl += `?${params.toString()}`;
    }

    globalThis.location.href = loginUrl;
  }, []);

  const signup = useCallback(async () => {
    const params = new URLSearchParams();
    params.set("screen_hint", "signup");
    params.set("returnTo", "/onboarding");
    globalThis.location.href = `/auth/login?${params.toString()}`;
  }, []);

  const logout = useCallback(async () => {
    ClearAllCache();
    posthog?.reset();
    globalThis.location.href = "/auth/logout";
  }, [posthog]);

  const switchOrg = useCallback(async (orgId: string, returnTo = "/") => {
    const params = new URLSearchParams();
    params.set("organization", orgId);
    params.set("returnTo", isSafeInternalPath(returnTo) ? returnTo : "/");
    globalThis.location.href = `/auth/login?${params.toString()}`;
  }, []);

  const refreshAuthSeed = useCallback(() => {
    router.refresh();
  }, [router]);

  useEffect(() => {
    const onTokenRefreshed = () => {
      refreshAuthSeed();
    };
    window.addEventListener(TOKEN_REFRESHED_EVENT, onTokenRefreshed);
    return () => window.removeEventListener(TOKEN_REFRESHED_EVENT, onTokenRefreshed);
  }, [refreshAuthSeed]);

  // ── Soft token refresh (no page reload!) ────────────────────────────────

  const renewToken = useCallback(async (options?: { force?: boolean }) => {
    try {
      const headers: HeadersInit = {};
      if (options?.force) {
        headers[FORCE_TOKEN_REFRESH_HEADER] = "1";
      }

      const response = await fetch("/api/auth/refresh", {
        method: "POST",
        credentials: "include",
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          await logout();
        }
        return;
      }

      refreshAuthSeed();
    } catch (error) {
      console.error("Failed to refresh token:", error);
    }
  }, [logout, refreshAuthSeed]);

  // ── Agency status ───────────────────────────────────────────────────────

  const fetchAgencyStatus = useCallback(async () => {
    try {
      const newStatus = await apiLive<AgencyStatusResponse>("/api/settings/agency/status");
      setAgencyStatus(newStatus);
    } catch (ex) {
      console.error("Failed to fetch agency status", ex);
      setAgencyStatus({
        status: SubscriptionStatus.trialing,
        canAccess: true,
        restrictions: GetRestrictions(null),
        flags: { needs_signing: false },
        hasUsage: false,
      });
    }
  }, []);

  const renewStatus = useCallback(async () => {
    try {
      await fetchAgencyStatus();
    } catch (ex) {
      console.error("Failed to renew agency status", ex);
    }
  }, [fetchAgencyStatus]);

  // Fetch agency status once on mount when we have a valid user
  useEffect(() => {
    if (user?.agencyId && user?.orgId && !agencyStatusFetched.current) {
      agencyStatusFetched.current = true;
      fetchAgencyStatus();
    }
  }, [user, fetchAgencyStatus]);

  // ── PostHog / Canny identification ──────────────────────────────────────

  const identifyUser = useCallback(
    (u: User, status?: AgencyStatusResponse | null) => {
      if (u.id === identifiedUserId) return;
      setIdentifiedUserId(u.id);

      if (!isEuropeRegion() && posthog) {
        posthog.identify(u.id, {
          email: u.email,
          name: u.name,
          orgId: u.orgId,
          agencyId: u.agencyId,
          subscription: status,
          isFreeTrial:
            status?.status === SubscriptionStatus.none ||
            status?.status === SubscriptionStatus.trialing,
        });
        posthog.group("agency", u.agencyId ? u.agencyId : "onboarding");
        posthog.group("subscription", status ? status.status : SubscriptionStatus.none);
      }

      identifyCannyUser(u, status);
    },
    [identifiedUserId, posthog]
  );

  useEffect(() => {
    if (user) {
      identifyUser(user, agencyStatus);
      setAuthzDeniedIdentity({
        orgId: user.agencyId,
        auth0OrgId: user.orgId,
        auth0UserId: user.id,
        gailUserId: user.userId,
      });
    } else {
      setAuthzDeniedIdentity(null);
    }
  }, [user, agencyStatus, identifyUser]);

  // Galactus scopes for flag targeting (all regions). EU gets these only — no PII.
  useEffect(() => {
    if (!user || !posthog) return;

    if (isEuropeRegion()) {
      posthog.setPersonPropertiesForFlags({
        [POSTHOG_PERMISSIONS_FLAG_PROPERTY]: scopes,
      });
    } else {
      posthog.setPersonPropertiesForFlags({
        name: user.name,
        email: user.email,
        orgId: user.orgId,
        agencyId: user.agencyId,
        subscription: agencyStatus,
        isFreeTrial:
          agencyStatus?.status === SubscriptionStatus.none ||
          agencyStatus?.status === SubscriptionStatus.trialing,
        [POSTHOG_PERMISSIONS_FLAG_PROPERTY]: scopes,
      });
    }

    posthog.reloadFeatureFlags();
  }, [user, scopes, agencyStatus, posthog]);

  // ── Document title ──────────────────────────────────────────────────────

  useEffect(() => {
    if (!user) {
      document.title = "Gail | Login";
    }
  }, [user]);

  // ── Context value ───────────────────────────────────────────────────────

  const value = useMemo<AuthContextValue>(
    () => ({
      login,
      signup,
      logout,
      renewToken,
      renewStatus,
      switchOrg,
      refreshAuthSeed,
      user,
      isAuthenticated,
      isLoading: false, // Never loading — data arrives with HTML
      subscription: agencyStatus ?? DEFAULT_SUBSCRIPTION,
      isFreeTrial: agencyStatus?.status === SubscriptionStatus.none,
      role,
      rawRoles,
      productRolesState,
      permissions,
      scopes,
      authz,
      scopesAuthoritative,
      provisioningPending,
      permissionsDegraded,
    }),
    [
      login,
      signup,
      logout,
      renewToken,
      renewStatus,
      switchOrg,
      refreshAuthSeed,
      user,
      isAuthenticated,
      agencyStatus,
      role,
      rawRoles,
      productRolesState,
      permissions,
      scopes,
      authz,
      scopesAuthoritative,
      provisioningPending,
      permissionsDegraded,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
